// The Speedy Method — long-form deep-dive content for /method and /method/:slug.
// Primary keywords: "court resurfacing", "line marking sydney".

export const METHOD_STEPS = [
  {
    slug: "consultation",
    n: "01",
    title: "Consultation",
    icon: "MessageSquare",
    tagline: "A free on-site court assessment — before a single dollar is committed.",
    timeline: "30–60 minutes on site · findings within 48 hours",
    image: "worn",
    showcase: "backyardBlue",
    showcaseCaption: "Sky blue on slate grey — a Sydney backyard half-court delivered after a 40-minute consultation.",
    seoTitle: "Court Resurfacing Consultation Sydney",
    seoDescription:
      "Step 1 of The Speedy Method: a free on-site court resurfacing consultation across Sydney & NSW. We assess cracking, drainage, coating adhesion and line marking compliance, then give you honest options — not a sales pitch.",
    intro:
      "Every court tells a story before anyone picks up a roller. Step one of The Speedy Method is a free, no-obligation site visit where we read that story — the substrate, the drainage, the coating history, the way water sits after Sydney rain — so the resurfacing plan is built on evidence instead of assumptions.",
    stats: [
      { k: "Cost", v: "Free" },
      { k: "On site", v: "30–60 min" },
      { k: "Report back", v: "Within 48 hrs" },
    ],
    whatHappens: [
      {
        title: "We walk the whole surface with you",
        text: "Not a glance from the fence line. We cover the court corner to corner, note every crack, blister, low spot and worn key, and photograph the areas that will drive the scope of works.",
      },
      {
        title: "Substrate and crack diagnosis",
        text: "There is a world of difference between a hairline shrinkage crack and a live structural crack moving with the slab. We identify which you have, because filling the wrong one guarantees the crack returns through your new surface within a season.",
      },
      {
        title: "Drainage and fall assessment",
        text: "Ponding is the number one killer of acrylic court coatings in NSW. We check falls, low points and where water tracks after heavy rain, and flag anything that needs correcting before coatings go anywhere near it.",
      },
      {
        title: "Coating adhesion testing",
        text: "We check whether the existing coating is still bonded or lifting. If it is peeling, resurfacing over the top just buys you a prettier failure — so we tell you plainly when removal is the right call.",
      },
      {
        title: "Line marking and compliance review",
        text: "We measure the playable area against the standards for your sport, so you know up front whether a full-size, modified or multi-sport layout genuinely fits the space you have.",
      },
      {
        title: "Your goals, your budget, your timing",
        text: "School holidays, competition season, a wedding in the backyard — we ask what the court is for and when you need it, then shape the recommendation around real life rather than an ideal calendar.",
      },
    ],
    includes: [
      "Free on-site inspection anywhere we service in Sydney & NSW",
      "Crack, blister and surface defect mapping",
      "Drainage, fall and ponding assessment",
      "Existing coating adhesion check",
      "Playable-area measurement for your chosen sports",
      "Honest advice on repair vs resurface vs rebuild",
    ],
    deliverables: [
      "A plain-English summary of your court's actual condition",
      "Photos of the defects we found, so nothing is hidden",
      "Recommended surface system and colour direction",
      "Realistic timeframe and weather window",
      "An indicative budget range before any formal quote",
    ],
    mistakes: [
      "Quoting a court sight-unseen from a phone call or a photo",
      "Recommending a resurface when a targeted repair is genuinely enough",
      "Ignoring drainage because it is not our problem to fix",
      "Pressuring a decision on the spot with a discount that expires tonight",
    ],
    local:
      "We consult on courts from Sydney CBD and the Sutherland Shire out through Liverpool, Minto and Menangle, and down the coast to Wollongong, Nowra and Mollymook — plus Newcastle, Goulburn, Queanbeyan and Canberra. If you are searching for court resurfacing or line marking in Sydney, the consultation is where it starts.",
    faqs: [
      { q: "Is the court consultation really free?", a: "Yes. The on-site assessment across our Sydney and NSW service areas costs nothing and carries no obligation. You keep the findings whether you proceed with us or not." },
      { q: "How soon can you come out?", a: "Most Sydney metro consultations are booked within a few days. Regional NSW visits are grouped by area, so we will give you the next available window when you enquire." },
      { q: "Do I need to be there for the inspection?", a: "It helps enormously — five minutes of your context about how the court is used often changes the recommendation. If you cannot be there, we will document everything and walk you through it afterwards." },
      { q: "What if my court does not need resurfacing?", a: "Then we tell you. Sometimes a pressure clean, a crack repair or fresh line marking gets you another few years. We would rather earn the resurface later than sell you one you do not need." },
    ],
  },
  {
    slug: "preparation",
    n: "02",
    title: "Preparation",
    icon: "ClipboardList",
    tagline: "The plan, the colours, the itemised quote — every line spelled out before we start.",
    timeline: "Detailed quote and design within 2–5 business days",
    image: "speedySport",
    showcase: "tanTeal",
    showcaseCaption: "Tan and teal, specified and signed off at planning stage — then delivered exactly as drawn.",
    seoTitle: "Court Resurfacing Quote & Design Sydney",
    seoDescription:
      "Step 2 of The Speedy Method: a tailored court resurfacing plan and a clear, itemised quote. Surface system, colours, layout, line marking and timeline agreed in writing before work begins — Sydney & NSW.",
    intro:
      "Preparation is where most court projects are quietly won or lost. We turn the consultation findings into a written scope, a surface system chosen for your conditions, a colour and layout design you can actually see, and an itemised quote with no vague allowances hiding inside it.",
    stats: [
      { k: "Quote turnaround", v: "2–5 days" },
      { k: "Pricing", v: "Fully itemised" },
      { k: "Surprises", v: "Zero" },
    ],
    whatHappens: [
      {
        title: "The surface system is specified, not guessed",
        text: "Cushioned or standard acrylic, number of coats, primer, patch compounds and crack treatment are all chosen for your substrate, your sport and Australian weather — then written down so you know precisely what you are buying.",
      },
      {
        title: "Colours and layout are designed with you",
        text: "Base, key, border and line colours are selected together, and you can trial combinations yourself in our online court customiser before we lock anything in. Schools and clubs can bring crests, logos and brand colours to the surface.",
      },
      {
        title: "Line marking is planned to standard",
        text: "Single sport or multi-sport, full size or modified — the layout is set out to the relevant dimensions with a colour hierarchy that keeps every sport legible instead of a confusing tangle of overlapping lines.",
      },
      {
        title: "You get an itemised quote",
        text: "Preparation, repairs, coatings, line marking and graphics appear as separate line items. If you want to stage the work across two budgets or two financial years, we show you exactly where the natural break points are.",
      },
      {
        title: "The schedule is built around weather and access",
        text: "Acrylic coatings need dry, workable conditions and cure time. We plan around forecasts, school terms, competition fixtures and site access so the court is offline for the shortest sensible window.",
      },
      {
        title: "Compliance, insurance and site paperwork",
        text: "As a CM3-certified contractor we supply the safety documentation, insurances, SWMS and inductions that schools, councils and facility managers need — sorted before day one, not chased afterwards.",
      },
    ],
    includes: [
      "Written scope of works with the surface system named",
      "Custom colour and layout design",
      "Line marking plan to sport-specific dimensions",
      "Itemised, staged-optional pricing",
      "Weather-aware programme and access plan",
      "CM3 safety documentation, SWMS and insurances",
    ],
    deliverables: [
      "A clear, itemised quote you can compare line by line",
      "Your colour scheme and court layout confirmed in writing",
      "A start date and a realistic completion date",
      "Named contact for the duration of the project",
      "All compliance paperwork your site requires",
    ],
    mistakes: [
      "Single-figure lump-sum quotes with nothing itemised",
      "Vague allowances that mysteriously grow mid-project",
      "Promising a colour we have not confirmed is available",
      "Booking a start date the weather was never going to allow",
    ],
    local:
      "We prepare and price court resurfacing and line marking across Sydney, the Shire, Liverpool, Wollongong, Newcastle, Canberra and regional NSW. Local knowledge matters: coastal salt air, western Sydney heat and Southern Highlands frost all change how a surface should be specified.",
    faqs: [
      { q: "How long does the quote take?", a: "Usually two to five business days after the site visit. Larger multi-court precincts with staged budgets can take a little longer because we price each stage separately." },
      { q: "Can I choose any colour I like?", a: "Within the range of court-grade acrylic colours, yes — and you can preview combinations in our online customiser first. Custom logos, crests and centre graphics are part of what we do, not an afterthought." },
      { q: "Can the work be split across two budgets?", a: "Often, yes. We identify sensible stages — repairs and preparation now, coatings and marking later — so you can spread the cost without compromising the final result." },
      { q: "Do you provide safety documentation for schools and councils?", a: "Yes. We are a CM3-certified contractor, fully licensed and insured, and we supply SWMS, insurances and induction paperwork as standard." },
    ],
  },
  {
    slug: "execution",
    n: "03",
    title: "Execution",
    icon: "Target",
    tagline: "Substrate prepared, defects repaired, premium acrylic laid, lines marked to the millimetre.",
    timeline: "Most courts complete in 3–7 working days, weather permitting",
    image: "speedyBball",
    showcase: "redSchool",
    showcaseCaption: "A school court rebuilt in a single holiday window — high-grip coating, crisp multi-sport marking.",
    seoTitle: "Court Resurfacing & Line Marking Sydney",
    seoDescription:
      "Step 3 of The Speedy Method: substrate preparation, crack repair, layered premium acrylic coatings and millimetre-perfect line marking in Sydney. See exactly how a Speedy court is built, day by day.",
    intro:
      "This is the part everyone pictures, and the part where shortcuts cost you years. Our crew prepares the substrate, repairs defects properly, builds the surface in layers with correct cure time between them, and finishes with line marking measured and masked to the millimetre.",
    stats: [
      { k: "Typical duration", v: "3–7 days" },
      { k: "Coating", v: "Layered acrylic" },
      { k: "Marking tolerance", v: "Millimetre" },
    ],
    whatHappens: [
      {
        title: "Deep clean and surface preparation",
        text: "High-pressure cleaning strips dirt, moss, chalking and loose material. Failed coating is removed rather than coated over, because new acrylic can only be as sound as whatever sits beneath it.",
      },
      {
        title: "Crack repair and patching",
        text: "Cracks are cut out, cleaned and filled with the right compound for the movement type. Low spots and ponding areas are levelled with patch binder so water sheds instead of sitting and lifting the coating.",
      },
      {
        title: "Priming and binding",
        text: "Where the substrate calls for it, we prime to guarantee adhesion. This is the invisible step that decides whether your surface is still bonded in five years or peeling in eighteen months.",
      },
      {
        title: "Layered acrylic build-up",
        text: "Resurfacer and colour coats are applied in multiple passes, each given proper cure time. Layering is what delivers even colour, consistent ball response and the grip level chosen for your sport.",
      },
      {
        title: "Precision line marking",
        text: "Line marking is the craft the Speedy name was built on. Every layout is measured, chalked, masked and hand-rolled to sport-specific dimensions with sharp, straight, durable edges — no wobble, no bleed.",
      },
      {
        title: "Logos, crests and centre graphics",
        text: "Custom centre logos, school crests, club emblems and feature graphics are cut and applied on the finished surface, turning a functional court into something people photograph.",
      },
      {
        title: "A tidy, safe, communicative site",
        text: "The area is contained and signed, the site is left clean each day, and you are told what is happening tomorrow. Court work should not disrupt a school, a club or a household any more than it must.",
      },
    ],
    includes: [
      "High-pressure clean and full surface preparation",
      "Structural and hairline crack repair",
      "Levelling of low spots and ponding areas",
      "Priming where the substrate requires it",
      "Multiple layered acrylic colour coats",
      "Standards-based precision line marking",
      "Custom logos, crests and centre graphics",
    ],
    deliverables: [
      "A fully cured, evenly coloured playing surface",
      "Crisp, accurate markings for every sport specified",
      "Your logo or crest applied to a professional standard",
      "A clean, safe site handed back each evening",
      "Daily progress updates from your named contact",
    ],
    mistakes: [
      "Coating over failed or peeling paint to save a day",
      "Rushing cure time between coats because rain is forecast",
      "Marking lines by eye rather than measuring the layout",
      "Leaving tools, tape and debris on a client's site overnight",
    ],
    local:
      "Our crews resurface and mark courts right across Sydney and NSW — schools in the Shire and western Sydney, clubs in Wollongong and Newcastle, councils through Goulburn, Queanbeyan and Canberra. Because we operate round the clock, work can run outside school hours or trading hours where a site needs it.",
    faqs: [
      { q: "How long is my court out of action?", a: "Most courts are complete within three to seven working days, then need a short cure period before play. Multi-court precincts take longer but can often be staged so part of the facility stays open." },
      { q: "What happens if it rains?", a: "We pause. Acrylic coatings must go down on a dry, workable surface, so we plan around forecasts and resume as soon as conditions allow. We would rather move a date than compromise the finish." },
      { q: "Can you work outside school or business hours?", a: "Yes. Round-the-clock scheduling is one of the reasons schools and facilities use us — we can work evenings, weekends and holiday periods to keep disruption down." },
      { q: "Will old lines show through the new surface?", a: "Not when the surface is built correctly. Proper preparation and enough colour-coat layers fully obscure previous markings before the new layout goes on." },
    ],
  },
  {
    slug: "final-inspection",
    n: "04",
    title: "Final Inspection",
    icon: "BadgeCheck",
    tagline: "A full quality check against Australian standards, then a handover you can hold us to.",
    timeline: "Walk-through on completion · care guidance at handover",
    image: "greenNetball",
    showcase: "greenNetball",
    showcaseCaption: "A multi-court netball precinct signed off to competition standard at final inspection.",
    seoTitle: "Court Handover & Quality Inspection Sydney",
    seoDescription:
      "Step 4 of The Speedy Method: a full quality inspection against Australian standards, joint client walk-through, line marking verification and surface care guidance at handover. Sydney & NSW court resurfacing.",
    intro:
      "A court is not finished when the last coat dries — it is finished when it has been checked, walked, signed off and explained. Final inspection is our quality gate and your protection: we verify the work against Australian standards, then hand over a court built to perform and impress for years.",
    stats: [
      { k: "Inspection", v: "Line by line" },
      { k: "Walk-through", v: "With you" },
      { k: "Aftercare", v: "Advice included" },
    ],
    whatHappens: [
      {
        title: "Coating quality verification",
        text: "We check coverage, colour consistency, texture and grip across the whole surface — including the edges and corners where rushed jobs give themselves away.",
      },
      {
        title: "Line marking measured and confirmed",
        text: "Every marked dimension is re-checked against the standard for the sport. Straightness, width, edge sharpness and colour separation are all verified before we call it complete.",
      },
      {
        title: "Drainage checked in the real world",
        text: "We confirm water sheds the way it should and that no new low spots were introduced during patching, so the surface you paid for is not undermined by the first big Sydney downpour.",
      },
      {
        title: "Joint walk-through with you",
        text: "We walk the court together. Anything you are not happy with gets listed and addressed — this is the moment to speak up, and we want you to use it.",
      },
      {
        title: "Site cleared and made ready for play",
        text: "Masking, equipment, waste and signage are removed, fixtures and nets are reinstated where applicable, and the court is left ready for its first game.",
      },
      {
        title: "Care and maintenance guidance",
        text: "You get straightforward advice on cure time before first use, cleaning, what to keep off the surface, and when a light re-mark or repaint will keep it looking new. Courts that are cared for simply last longer.",
      },
    ],
    includes: [
      "Full-surface coating quality inspection",
      "Line marking dimension and edge verification",
      "Drainage and ponding re-check",
      "Joint client walk-through and defect list",
      "Complete site clean-up and reinstatement",
      "Written surface care and maintenance guidance",
    ],
    deliverables: [
      "A court signed off against Australian standards",
      "Any snag items resolved before we leave",
      "Finished project photography of your court",
      "Cure time and first-use instructions",
      "Ongoing maintenance and re-mark recommendations",
    ],
    mistakes: [
      "Calling a court complete without walking it with the client",
      "Leaving snag items to be chased weeks later",
      "Handing over with no advice on cure time or care",
      "Disappearing the moment the invoice is issued",
    ],
    local:
      "Whether the court is a backyard half-court in Sydney's east, a school precinct in Liverpool or a club facility in Wollongong, Newcastle or Canberra, the handover standard does not change. Court resurfacing and line marking in Sydney should end with a signature, not a shrug.",
    faqs: [
      { q: "How long before we can play on it?", a: "Coatings need a short cure period after the final coat — we give you the exact window at handover, since it depends on the system used and the weather during application." },
      { q: "What if I spot a problem after you leave?", a: "Tell us. Anything genuinely related to our workmanship gets looked at. That is the whole point of doing a documented walk-through and handover." },
      { q: "How do I keep the court looking new?", a: "Keep it clean and free of leaf litter and moss, keep sharp or heavy items off the surface, and plan a light re-mark or repaint when colours start to dull. We set all of this out at handover." },
      { q: "Do you offer ongoing maintenance or re-marking?", a: "Yes. Many schools, clubs and councils bring us back for periodic line re-marking and repainting long before a full resurface is needed." },
    ],
  },
];

export const methodBySlug = (slug) => METHOD_STEPS.find((s) => s.slug === slug);
